import { cva, type VariantProps } from 'class-variance-authority';
import { readerAvatarOptionForSlug, type ReaderAvatarSlug } from '@/constants/reader-avatars';
import { cn } from '@/lib/cn';

/**
 * ReaderAvatar — the bundled animal avatar used on the reader selector pill,
 * the Readers page, and the personalized shelf header. Older cached readers
 * may not have an avatar slug yet, so we keep the deterministic initial badge
 * as a fallback.
 */
const avatar = cva(
  'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br font-bold text-white',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-[11px]',
        sm: 'h-8 w-8 text-[12px]',
        md: 'h-10 w-10 text-[14px]',
        lg: 'h-14 w-14 text-[20px]',
        xl: 'h-20 w-20 text-[28px]',
      },
      tone: {
        orange: 'from-or-orange-300 to-or-orange-600',
        blue: 'from-or-blue-400 to-or-blue-600',
        rose: 'from-or-rose-100 to-or-rose-500',
        yellow: 'from-or-yellow-200 to-or-orange-500',
        green: 'from-or-green-100 to-or-green-700',
        cream: 'from-or-cream-100 to-or-orange-300',
      },
    },
    defaultVariants: { size: 'md', tone: 'orange' },
  },
);

export type ReaderAvatarTone = NonNullable<VariantProps<typeof avatar>['tone']>;

const TONE_ORDER: ReaderAvatarTone[] = ['orange', 'blue', 'rose', 'yellow', 'green', 'cream'];

/**
 * Deterministic tone picker keyed off the reader name. Using the sum of
 * charCodes (vs. the first character alone) means common first letters
 * like "E" don't all collapse onto the same tone.
 */
export function toneForName(name: string): ReaderAvatarTone {
  if (!name) return 'orange';
  let hash = 0;
  for (const ch of name) hash = (hash + ch.charCodeAt(0)) % 1024;
  return TONE_ORDER[hash % TONE_ORDER.length]!;
}

export interface ReaderAvatarProps extends VariantProps<typeof avatar> {
  name: string;
  avatarSlug?: string | null;
  className?: string;
}

export function ReaderAvatar({ name, avatarSlug, size, tone, className }: ReaderAvatarProps) {
  const initial = (name.trim().charAt(0) || '?').toUpperCase();
  const effectiveTone = tone ?? toneForName(name);
  const option = avatarSlug ? readerAvatarOptionForSlug(avatarSlug) : null;
  return (
    <span className={cn(avatar({ size, tone: effectiveTone }), className)} aria-hidden>
      {option ? (
        <img src={option.src} alt="" className="h-full w-full object-cover" draggable={false} />
      ) : (
        initial
      )}
    </span>
  );
}

export type { ReaderAvatarSlug };
